/* Preferências locais (somente neste navegador): tema, nome de exibição e modo de estado.
   Mesma chave da Etapa 0, para manter o que já estava salvo. */
import { useSyncExternalStore } from 'react';

export type Theme = 'system' | 'light' | 'dark';
export type StateMode = 'real' | 'demo' | 'loading' | 'error';
/* voiceName: voz escolhida para o Jarvis falar; vazio = escolha automática (masculina). */
export interface Prefs { theme: Theme; stateMode: StateMode; displayName: string; voiceName: string }

export const STATE_MODES: StateMode[] = ['real', 'demo', 'loading', 'error'];
export const STATE_MODE_LABEL: Record<StateMode, string> = {
  real: 'Dados reais', demo: 'Demonstração', loading: 'Carregando', error: 'Erro'
};

const KEY = 'jarvis.prefs.v1';
const DEFAULTS: Prefs = { theme: 'system', stateMode: 'real', displayName: '', voiceName: '' };
const THEMES: Theme[] = ['system', 'light', 'dark'];

function sanitize(raw: unknown): Prefs {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const mode = r.stateMode === 'empty' ? 'real' : r.stateMode;
  return {
    theme: THEMES.includes(r.theme as Theme) ? (r.theme as Theme) : DEFAULTS.theme,
    stateMode: STATE_MODES.includes(mode as StateMode) ? (mode as StateMode) : DEFAULTS.stateMode,
    displayName: typeof r.displayName === 'string' ? r.displayName.slice(0, 40) : '',
    voiceName: typeof r.voiceName === 'string' ? r.voiceName.slice(0, 200) : ''
  };
}

function read(): Prefs {
  try { return sanitize(JSON.parse(localStorage.getItem(KEY) ?? '{}')); } catch { return { ...DEFAULTS }; }
}

let prefs = read();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export const prefsStore = {
  get: () => prefs,
  set(patch: Partial<Prefs>) {
    prefs = sanitize({ ...prefs, ...patch });
    try { localStorage.setItem(KEY, JSON.stringify(prefs)); } catch { /* armazenamento indisponível: fica só em memória */ }
    emit();
  },
  reset() {
    prefs = { ...DEFAULTS };
    try { localStorage.removeItem(KEY); } catch { /* idem */ }
    emit();
  },
  subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }
};

export function usePrefs(): Prefs {
  return useSyncExternalStore(prefsStore.subscribe, prefsStore.get);
}

const darkQuery = () => window.matchMedia('(prefers-color-scheme: dark)');

export function useSystemDark(): boolean {
  return useSyncExternalStore(
    (cb) => { const q = darkQuery(); q.addEventListener('change', cb); return () => q.removeEventListener('change', cb); },
    () => darkQuery().matches
  );
}

export function useIsDark(): boolean {
  const { theme } = usePrefs();
  const systemDark = useSystemDark();
  return theme === 'dark' || (theme === 'system' && systemDark);
}
